import { create } from 'zustand'
import { DEFAULT_FOCUS_MODE, type FocusMode } from '@renderer/utils/cameras/prefocus'
import type { CaptureFormat } from '@renderer/utils/captureEncoding'
import { persist } from 'zustand/middleware'
import { generateDeviceId } from '@renderer/utils/deviceUtils'
import { mergeNestedDefaults } from './settingsMerge'
import { PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS } from '@renderer/utils/printResultMinDisplay'

// Define ResizeQuality type
type ResizeQuality = 'pixelated' | 'low' | 'medium' | 'high'

/**
 * How this booth takes card payments — device-local, because it names a machine
 * standing next to THIS booth. `manual`: the guest taps "Pay by card", staff run the
 * card on their own EDC and confirm on a PIN-gated step; the session records the
 * method, the terminal and the approval code. `connected`: the booth pushes the
 * amount to the terminal itself and the result comes back on its own — a
 * Mercado Pago Point terminal (needs a Mercado Pago project) or a Nayax reader
 * (needs a Nayax account in Studio), paired in `terminalId`.
 */
export interface CardTerminalSettings {
  mode: 'none' | 'manual' | 'connected'
  /** Operator's label for the machine, e.g. "EDC BCA — front desk". Shown on the confirm step. */
  name: string
  /** Confirming requires the approval code off the EDC receipt. Off = an honour system. */
  requireApprovalCode: boolean
  /** Show Visa / Mastercard marks on the guest-facing button, so a tourist recognises it. */
  showCardMarks: boolean
  /**
   * `connected` mode: the terminal this booth pushes amounts to, as Mercado
   * Pago reports it (`NEWLAND_N950__N950NCB801293324`). Device-local like the
   * camera and printer slots, because the terminal stands next to THIS booth.
   * Optional: a booth that updated from a build without it has no value stored.
   */
  terminalId?: string
  /**
   * `connected` mode: which integration drives the terminal. Absent on booths
   * paired before Nayax existed, which all have a Mercado Pago Point terminal;
   * read it through `connectedTerminalProvider`.
   */
  provider?: 'mercadopago' | 'nayax'
}

// Define PreviewQuality interface
interface PreviewQuality {
  maxWidth: number
  maxHeight: number
  quality: number
  resizeQuality: ResizeQuality
  /** Cap live preview frame rate when not driving a recording canvas. Undefined = uncapped. */
  maxFps?: number
}

interface SettingsState {
  password?: string
  selectedPrinter?: string
  downloadLocal: boolean
  hasSeenSkipPaymentGuide: boolean
  autoStart: boolean
  autoLaunch: boolean
  defaultProjectId?: string
  /**
   * Hub the booth boots into, the hub twin of `defaultProjectId`. Only one of
   * the two drives autostart and this one wins (Projects.tsx) — a booth that
   * has been pointed at a hub must not silently start a single member instead.
   */
  defaultHubId?: string
  activationCode: string
  /**
   * Email of the account that owns the current activation code, recorded when the
   * server confirms the token on validate. Used to clear the token ONLY on a genuine
   * account switch (a different account signs in), never on plain logout.
   */
  licenseOwnerEmail?: string
  deviceId: string
  /** When true, Preview overlays stats.js FPS (use with Performance panel on target hardware). */
  debugFps: boolean
  useWebcam: boolean
  setPassword: (password?: string) => void
  setSelectedPrinter: (printer?: string) => void
  selectedCamera?: string
  setSelectedCamera: (camera?: string) => void
  /**
   * Durable camera history: the last non-empty selectedCamera value, NEVER cleared.
   * Every failure/eject path clears selectedCamera (tryToConnect catch, 4-strike
   * preview teardown, SettingsModal eject) — which used to leave booth re-entry
   * SILENT: no CameraPrompt, no settings, guest walks into a broken flow. View's
   * boot validation restores the selection from here when the device is still
   * physically attached, so unmanned booths get a one-tap (or, with reconnect v2,
   * automatic) reconnect instead of nothing.
   */
  lastSelectedCamera?: string
  selectedSecondaryCamera?: string
  /**
   * Which camera each project shoots on, on THIS machine.
   *
   * The printer twin of this lives in printStore as `printerSlotByProject`;
   * both are deliberately local. A hub is shared by booths with different
   * hardware, so the assignment cannot live in the hub document — booth A's
   * second camera is not booth B's.
   *
   * Values are the vocabulary `sessionState.userPreferences.selectedCamera`
   * has always used, which every capture, retake, filter and preview already
   * reads as its `cameraKey`. Absent = the booth decides, as it does today.
   */
  cameraSlotByProject: Record<string, 'primary' | 'secondary'>
  setCameraSlotForProject: (projectId: string, slot?: 'primary' | 'secondary') => void
  setSelectedSecondaryCamera: (camera?: string) => void
  cameraConnectionKey: number // Increments on each camera connection to force Preview remount
  incrementCameraConnectionKey: () => void
  setDownloadLocal: (download: boolean) => void
  setHasSeenSkipPaymentGuide: (seen: boolean) => void
  setAutoStart: (enabled: boolean) => void
  setAutoLaunch: (enabled: boolean) => void
  setDefaultProjectId: (projectId?: string) => void
  setDefaultHubId: (hubId?: string) => void
  setActivationCode: (code?: string) => void
  setLicenseOwnerEmail: (email?: string) => void
  setDebugFps: (enabled: boolean) => void
  setUseWebcam: (enabled: boolean) => void
  showCameraQualityWarnings: boolean
  setShowCameraQualityWarnings: (enabled: boolean) => void
  cameraSettings: {
    captureRatio: number | 'auto' | undefined
    mirror: boolean
    useShutterCapture: boolean
    applyFilterOnPreview: boolean
    previewQuality?: PreviewQuality
    captureQuality?: PreviewQuality
    /**
     * File format of the saved photo — what the session, strip and upload queue carry.
     * Undefined until the operator picks one (and on every booth persisted before the
     * setting existed); readers go through `resolveCaptureFormat`, which applies the
     * booth default — PNG, or JPEG under the capture_jpeg_default flag. Kept unset rather
     * than seeded, so the flag can move every "never chosen" camera without touching an
     * operator's explicit choice.
     */
    captureFormat?: CaptureFormat
    /** Rotate the live preview display (CSS). Does not affect the saved image. */
    previewRotation: number
    /** Rotate the saved/captured image pixels. Independent from previewRotation. */
    captureRotation: number
    autoBrightenLiveViewForFlash: boolean
    liveViewShutterSpeedOverride?: number
    postShutterLiveviewDelayMs?: number
    /**
     * When the booth drives autofocus ahead of a shutter capture. Optional because
     * booths persisted this object before the setting existed; readers go through
     * `normaliseFocusMode`, which falls back to the previous behaviour.
     */
    focusMode?: FocusMode
    /**
     * Hold the shutter until the countdown's autofocus drive has finished, instead of
     * firing when the countdown ends. Optional for booths persisted before it existed;
     * readers go through `waitForFocusEnabled`, which falls back to OFF — the previous
     * behaviour, where the shutter never waits.
     */
    waitForFocusBeforeCapture?: boolean
  }
  setCameraSettings: (settings: Partial<SettingsState['cameraSettings']>) => void
  secondaryCameraSettings: {
    captureRatio: number | 'auto' | undefined
    mirror: boolean
    useShutterCapture: boolean
    applyFilterOnPreview: boolean
    previewQuality?: PreviewQuality
    captureQuality?: PreviewQuality
    /**
     * File format of the saved photo — what the session, strip and upload queue carry.
     * Undefined until the operator picks one (and on every booth persisted before the
     * setting existed); readers go through `resolveCaptureFormat`, which applies the
     * booth default — PNG, or JPEG under the capture_jpeg_default flag. Kept unset rather
     * than seeded, so the flag can move every "never chosen" camera without touching an
     * operator's explicit choice.
     */
    captureFormat?: CaptureFormat
    /** Rotate the live preview display (CSS). Does not affect the saved image. */
    previewRotation: number
    /** Rotate the saved/captured image pixels. Independent from previewRotation. */
    captureRotation: number
    autoBrightenLiveViewForFlash: boolean
    liveViewShutterSpeedOverride?: number
    postShutterLiveviewDelayMs?: number
    /**
     * When the booth drives autofocus ahead of a shutter capture. Optional because
     * booths persisted this object before the setting existed; readers go through
     * `normaliseFocusMode`, which falls back to the previous behaviour.
     */
    focusMode?: FocusMode
    /**
     * Hold the shutter until the countdown's autofocus drive has finished, instead of
     * firing when the countdown ends. Optional for booths persisted before it existed;
     * readers go through `waitForFocusEnabled`, which falls back to OFF — the previous
     * behaviour, where the shutter never waits.
     */
    waitForFocusBeforeCapture?: boolean
  }
  setSecondaryCameraSettings: (settings: Partial<SettingsState['secondaryCameraSettings']>) => void
  enableScreensaver: boolean
  screensaverTimeout: number
  /** When true (browser only), reduces pull-to-refresh and overscroll navigation. Default off. */
  suppressBrowserGestures: boolean
  setSuppressBrowserGestures: (enabled: boolean) => void
  cardTerminal: CardTerminalSettings
  setCardTerminal: (patch: Partial<CardTerminalSettings>) => void
  /**
   * Minimum seconds the processing screen stays open before Result after print work completes (0 = off).
   * Clamped 0–PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS.
   */
  printResultMinDisplaySeconds: number
  setPrintResultMinDisplaySeconds: (seconds: number) => void
  setSetting: (key: string, value: unknown) => void
}

/**
 * Captured BEFORE the store below is created (and before persist can write):
 * whether a persisted settings blob existed at boot. The device-identity sync
 * in App bootstrap uses this to tell an ESTABLISHED deviceId apart from one
 * freshly minted by the default initializer — only a fresh store may accept a
 * recovered id from the main process; an established id is never replaced.
 */
export const settingsStoreWasFresh = ((): boolean => {
  try {
    return typeof window === 'undefined' || window.localStorage.getItem('settings-v2') == null
  } catch {
    // Storage unreadable — assume established so we never overwrite a
    // potentially-bound deviceId.
    return false
  }
})()

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      downloadLocal: true, // Default to true
      hasSeenSkipPaymentGuide: false,
      autoStart: true, // Default to true
      autoLaunch: false, // Default to false
      defaultProjectId: undefined,
      defaultHubId: undefined,
      activationCode: '',
      licenseOwnerEmail: undefined,
      // Fresh installs only — a persisted deviceId always wins on rehydrate and is
      // frozen forever after (subscription bindings depend on it; see deviceUtils.ts).
      deviceId: generateDeviceId(),
      debugFps: false, // Default to false
      useWebcam: true, // Default to true
      showCameraQualityWarnings: true, // Default to true to maintain current behavior
      cameraSettings: {
        captureRatio: 'auto' as const, // Changed to 'auto' for no cropping behavior
        mirror: true,
        useShutterCapture: false, // Default to false (use preview capture) - inverted from usePreviewAsCapture: true
        applyFilterOnPreview: true,
        previewQuality: {
          maxWidth: 1280, // Default medium quality
          maxHeight: 720,
          quality: 0.8,
          resizeQuality: 'medium' as ResizeQuality
        },
        captureQuality: {
          maxWidth: 1920, // Default high quality
          maxHeight: 1080,
          quality: 1,
          resizeQuality: 'high' as ResizeQuality
        },
        previewRotation: 0,
        captureRotation: 0,
        autoBrightenLiveViewForFlash: false,
        liveViewShutterSpeedOverride: undefined,
        postShutterLiveviewDelayMs: undefined,
        focusMode: DEFAULT_FOCUS_MODE
      },
      secondaryCameraSettings: {
        captureRatio: 'auto' as const, // Changed to 'auto' for no cropping behavior
        mirror: true,
        useShutterCapture: false, // Default to false (use preview capture) - inverted from usePreviewAsCapture: true
        applyFilterOnPreview: true,
        previewQuality: {
          maxWidth: 1280, // Default medium quality
          maxHeight: 720,
          quality: 0.8,
          resizeQuality: 'medium' as ResizeQuality
        },
        captureQuality: {
          maxWidth: 1920, // Default high quality
          maxHeight: 1080,
          quality: 1,
          resizeQuality: 'high' as ResizeQuality
        },
        previewRotation: 0,
        captureRotation: 0,
        autoBrightenLiveViewForFlash: false,
        liveViewShutterSpeedOverride: undefined,
        postShutterLiveviewDelayMs: undefined,
        focusMode: DEFAULT_FOCUS_MODE
      },
      setPassword: (password): void => void set({ password }),
      setSelectedPrinter: (printer): void => void set({ selectedPrinter: printer }),
      cameraConnectionKey: 0,
      incrementCameraConnectionKey: (): void =>
        void set((state) => ({ cameraConnectionKey: (state.cameraConnectionKey || 0) + 1 })),
      setSelectedCamera: (camera): void =>
        void set((state) => ({
          selectedCamera: camera,
          // History survives clears: only overwritten by a new non-empty selection.
          lastSelectedCamera: camera || state.lastSelectedCamera
        })),
      setSelectedSecondaryCamera: (camera): void => void set({ selectedSecondaryCamera: camera }),
      cameraSlotByProject: {},
      setCameraSlotForProject: (projectId, slot): void =>
        void set((state) => {
          if (!projectId) return state
          const current = state.cameraSlotByProject ?? {}
          if (!slot) {
            if (!(projectId in current)) return state
            const next = { ...current }
            delete next[projectId]
            return { cameraSlotByProject: next }
          }
          return { cameraSlotByProject: { ...current, [projectId]: slot } }
        }),
      setDownloadLocal: (download): void => void set({ downloadLocal: download }),
      setHasSeenSkipPaymentGuide: (seen): void => void set({ hasSeenSkipPaymentGuide: seen }),
      setAutoStart: (enabled): void => void set({ autoStart: enabled }),
      setAutoLaunch: (enabled): void => void set({ autoLaunch: enabled }),
      // The two boot targets compete for one autostart, so each setter clears
      // the other. `setDefaultProjectId(undefined)` is the app's "stop
      // auto-booting" escape hatch (NotFound, FailedToInitialize, the
      // no-credits modal, Exit in SettingsModal) — it has to clear BOTH, or an
      // operator stuck in a broken hub could never reach the project list.
      setDefaultProjectId: (projectId): void =>
        void set({ defaultProjectId: projectId, defaultHubId: undefined }),
      setDefaultHubId: (hubId): void =>
        void set({ defaultHubId: hubId, ...(hubId ? { defaultProjectId: undefined } : {}) }),
      setActivationCode: (code): void => void set({ activationCode: code }),
      setLicenseOwnerEmail: (email): void => void set({ licenseOwnerEmail: email }),
      setDebugFps: (enabled): void => void set({ debugFps: enabled }),
      setUseWebcam: (enabled): void => void set({ useWebcam: enabled }),
      setShowCameraQualityWarnings: (enabled): void =>
        void set({ showCameraQualityWarnings: enabled }),
      enableScreensaver: false,
      screensaverTimeout: 60,
      suppressBrowserGestures: false,
      setSuppressBrowserGestures: (enabled): void => void set({ suppressBrowserGestures: enabled }),
      cardTerminal: {
        mode: 'none',
        name: '',
        requireApprovalCode: true,
        showCardMarks: true,
        terminalId: ''
      },
      setCardTerminal: (patch): void =>
        void set((state) => ({ cardTerminal: { ...state.cardTerminal, ...patch } })),
      printResultMinDisplaySeconds: 0,
      setPrintResultMinDisplaySeconds: (seconds): void =>
        void set({
          printResultMinDisplaySeconds: Math.max(
            0,
            Math.min(PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS, Math.floor(Number(seconds)) || 0)
          )
        }),
      setSetting: (key, value): void => void set({ [key]: value }),
      setCameraSettings: (settings): void =>
        void set((state) => ({
          cameraSettings: { ...state.cameraSettings, ...settings }
        })),
      setSecondaryCameraSettings: (settings): void =>
        void set((state) => ({
          secondaryCameraSettings: { ...state.secondaryCameraSettings, ...settings }
        }))
    }),
    {
      name: 'settings-v2', // Keep v2 to preserve other settings
      merge: (persistedState, currentState) => {
        const normalizeRotation = (value: unknown): number => {
          if (typeof value !== 'number' || Number.isNaN(value)) return 0
          const normalized = ((Math.round(value) % 360) + 360) % 360
          return normalized === 0 || normalized === 90 || normalized === 180 || normalized === 270
            ? normalized
            : 0
        }

        const persisted = persistedState as Partial<
          SettingsState & { _hasResetEjectCameraWhenIdle?: boolean; _hasMigratedShutterCapture?: boolean; _hasEnabledScreensaverByDefault?: boolean; _hasMigratedRotation?: boolean }
        >

        // Migration: Convert usePreviewAsCapture to useShutterCapture (invert logic)
        let migratedSettings = persisted || {}
        if (persisted && !persisted._hasMigratedShutterCapture) {
          // Migrate usePreviewAsCapture to useShutterCapture (invert boolean)
          if (persisted.cameraSettings && 'usePreviewAsCapture' in persisted.cameraSettings) {
            const oldValue = persisted.cameraSettings.usePreviewAsCapture as boolean
            migratedSettings = {
              ...persisted,
              cameraSettings: {
                ...persisted.cameraSettings,
                useShutterCapture: !oldValue, // Invert: true becomes false, false becomes true
                usePreviewAsCapture: undefined // Remove old property
              } as SettingsState['cameraSettings']
            }
          }
          if (persisted.secondaryCameraSettings && 'usePreviewAsCapture' in persisted.secondaryCameraSettings) {
            const oldValue = persisted.secondaryCameraSettings.usePreviewAsCapture as boolean
            migratedSettings = {
              ...migratedSettings,
              secondaryCameraSettings: {
                ...persisted.secondaryCameraSettings,
                useShutterCapture: !oldValue, // Invert: true becomes false, false becomes true
                usePreviewAsCapture: undefined // Remove old property
              } as SettingsState['secondaryCameraSettings']
            }
          }
          migratedSettings._hasMigratedShutterCapture = true
        }

        // Migration: Split old `rotation` field into previewRotation + captureRotation.
        // This is intentionally idempotent to avoid legacy values overriding user-configured
        // fields when migration flags are missing/corrupted.
        if (persisted) {
          const currentCamSettings = (migratedSettings.cameraSettings ||
            {}) as SettingsState['cameraSettings'] & { rotation?: number }
          const currentSecSettings = (migratedSettings.secondaryCameraSettings ||
            {}) as SettingsState['secondaryCameraSettings'] & { rotation?: number }
          const oldCamRotation = normalizeRotation(
            (persisted.cameraSettings as (SettingsState['cameraSettings'] & { rotation?: number }) | undefined)?.rotation
          )
          const oldSecRotation = normalizeRotation(
            (persisted.secondaryCameraSettings as (SettingsState['secondaryCameraSettings'] & { rotation?: number }) | undefined)?.rotation
          )
          const { rotation: _legacyCamRotation, ...camSettingsWithoutLegacyRotation } =
            currentCamSettings
          const { rotation: _legacySecRotation, ...secSettingsWithoutLegacyRotation } =
            currentSecSettings

          migratedSettings = {
            ...migratedSettings,
            cameraSettings: {
              ...camSettingsWithoutLegacyRotation,
              previewRotation:
                typeof currentCamSettings.previewRotation === 'number'
                  ? normalizeRotation(currentCamSettings.previewRotation)
                  : oldCamRotation,
              captureRotation:
                typeof currentCamSettings.captureRotation === 'number'
                  ? normalizeRotation(currentCamSettings.captureRotation)
                  : oldCamRotation
            } as SettingsState['cameraSettings'],
            secondaryCameraSettings: {
              ...secSettingsWithoutLegacyRotation,
              previewRotation:
                typeof currentSecSettings.previewRotation === 'number'
                  ? normalizeRotation(currentSecSettings.previewRotation)
                  : oldSecRotation,
              captureRotation:
                typeof currentSecSettings.captureRotation === 'number'
                  ? normalizeRotation(currentSecSettings.captureRotation)
                  : oldSecRotation
            } as SettingsState['secondaryCameraSettings'],
            _hasMigratedRotation: true
          }
        }

        // Migration: Preserve screensaver preference for existing users (default to false if unset)
        if (!migratedSettings._hasEnabledScreensaverByDefault) {
          migratedSettings = {
            ...migratedSettings,
            enableScreensaver: migratedSettings.enableScreensaver ?? false,
            _hasEnabledScreensaverByDefault: true
          }
        }

        // Retired setting: `ejectCameraWhenIdle` (idle-eject) is gone — Camera Rest
        // parks the camera instead, with no operator toggle. Strip the stale key off
        // rehydrated state so it can't linger in localStorage-backed settings.
        const {
          ejectCameraWhenIdle: _retiredEjectCameraWhenIdle,
          _hasResetEjectCameraWhenIdle: _retiredEjectMigrationFlag,
          ...migratedWithoutRetired
        } = migratedSettings as typeof migratedSettings & { ejectCameraWhenIdle?: boolean }

        // Deep, not shallow. A shallow spread replaces `cameraSettings`,
        // `cardTerminal` and the quality blocks wholesale with what the booth
        // saved, so every key added to them since then comes back `undefined`
        // instead of its default. See settingsMerge.ts.
        return {
          ...mergeNestedDefaults(currentState, migratedWithoutRetired),
          _hasEnabledScreensaverByDefault: true
        } as SettingsState & {
          _hasMigratedShutterCapture?: boolean
          _hasEnabledScreensaverByDefault?: boolean
          _hasMigratedRotation?: boolean
        }
      }
    }
  )
)
